import React, { useContext, useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import Field from "../core/ui/forms/field";
import SessionContext from "SessionContext";
import recolor_svg from "../core/utils/recolor_svg";
import { states } from "core/utils/list-of-states";
import LocationRebuildNotice from "../core/ui/location-rebuild-notice";
import Popover from "core/ui/popover";
import { Buffer } from "buffer";
import isEqual from "lodash.isequal";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import ConfirmAction from "../core/ui/confirm-action";
import { useGlobalState } from "hooks/useGlobalState";

export default function LocationForm(props) {
  const session = useContext(SessionContext);
  const access = session.appState.userCan;
  const { setIsGlobalStateSaved } = useGlobalState();

  const history = useHistory();

  const [location, setLocation] = useState({
    id: "",
    name: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip5: "",
    zip4: "",
    lat: "",
    lng: "",
    default_radius: "",
  });

  const [errors, setErrors] = useState({
    id: "",
    name: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip5: "",
    zip4: "",
    lat: "",
    lng: "",
    default_radius: "",
    locationType: "",
  });

  const [systemLocationTypeList, setSystemLocationTypeList] = useState([]);
  const [systemLocationTypeListLoaded, setSystemLocationTypeListLoaded] =
    useState(false);

  const [companyLocationTypeList, setCompanyLocationTypeList] = useState([]);
  const [companyLocationTypeListLoaded, setCompanyLocationTypeListLoaded] =
    useState(false);

  const [locationType, setLocationType] = useState();
  const [locationTypesById, setLocationTypesById] = useState({});
  const [loadedLocationTypeId, setLoadedLocationTypeId] = useState();
  const [locationDataChanged, setLocationDataChanged] = useState(false);
  const [succesLabel, setSuccessLabel] = useState("Saved!");
  const [showLocationPopover, setShowLocationPopover] = useState(false);
  const [loadedLocation, setLoadedLocation] = useState({});
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [showRebuildNotice, setShowRebuildNotice] = useState(false);

  function onCloseRebuildNotice() {
    setShowRebuildNotice(false);
    history.push("/locations");
  }

  function loadLocationTypes() {
    session
      .query("location/location-types", { companyId: 0 })
      .then((response) => {
        setSystemLocationTypeList(response);
        response.map((locType) =>
          setLocationTypesById((prevState) => ({
            ...prevState,
            [locType.id.toString()]: locType,
          }))
        );

        setSystemLocationTypeListLoaded(true);
      });
    session
      .query("location/location-types", {
        companyId: session.appState.activeCompany.id,
      })
      .then((response) => {
        setCompanyLocationTypeList(response);
        response.map((locType) =>
          setLocationTypesById((prevState) => ({
            ...prevState,
            [locType.id.toString()]: locType,
          }))
        );
        setCompanyLocationTypeListLoaded(true);
      });
  }

  useEffect(() => {
    const location_id = props.args.location_id;
    loadLocationTypes();
    if (location_id) {
      session
        .query("location/get", { id: location_id })
        .then((response) => {
          setLocation(response);
          setLoadedLocation(response);
          setLoadedLocationTypeId(response.location_type_id);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    systemLocationTypeListLoaded &&
      companyLocationTypeListLoaded &&
      setLocationType(locationTypesById[loadedLocationTypeId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadedLocationTypeId,
    systemLocationTypeListLoaded,
    companyLocationTypeListLoaded,
  ]);

  useEffect(() => {
    if(loadedLocation) {
      setLocationDataChanged(() => !isEqual(location, loadedLocation));
    }
  }, [location, loadedLocation])

  function askDeleteConfirmation(ev) {
    ev.stopPropagation();
    ev.preventDefault();
    setConfirmDeleteModal(true);
  }

  function onClickDelete(res) {
    if(res) {
      session
        .command(
          "location/delete",
          location,
          {},
          { defaultErrorHandling: false }
        )
        .then(() => {
          setConfirmDeleteModal(false);
          setShowUnsavedModal(false);
          setSuccessLabel("Deleted!");
          setShowRebuildNotice(true);
        })
        .catch((error) => {
          console.log("Location delete Failed", JSON.stringify(error));
        });
    } else {
      setConfirmDeleteModal(false);
    }
  }

  function onClickSave(ev) {
    ev.stopPropagation();
    ev.preventDefault();

    const newErrors = {};
    if (!location.name) newErrors.name = "Required";
    if (!location.address1) newErrors.address1 = "Required";
    if (!location.city) newErrors.city = "Required";
    if (!location.state) newErrors.state = "Required";
    if (!location.zip5) newErrors.zip5 = "Required";
    if (!location.default_radius) newErrors.default_radius = "Required";
    if (locationType === undefined) {
      newErrors.locationType = "Required";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    // remove whitespace from values
    const newLocation = { 
      ...location, 
      name: location.name.trim(),
      address1: location.address1.trim(),
      address2: location.address2 && location.address2.trim(),
      city: location.city.trim(),
      state: location.state.trim(),
      zip5: location.zip5.trim(),
      zip4: location.zip4.trim(),
    };

    session
      .command(
        "location/persist",
        newLocation,
        {},
        { defaultErrorHandling: false }
      )
      .then(() => {
        setShowUnsavedModal(false);
        setLocationDataChanged(false);
        setSuccessLabel("Saved!");
        setShowRebuildNotice(true);
      })
      .catch((error) => {
        console.log("Location persist Failed", JSON.stringify(error));
      });
  }

  function setName(ev) {
    setLocation({ ...location, name: ev.target.value });
    if (ev.target.value) {
      setErrors((prev) => delete prev.name);
    }
  }

  function setAddress1(ev) {
    setLocation({ ...location, address1: ev.target.value });
    if (ev.target.value) {
      setErrors((prev) => delete prev.address1);
    }
  }

  function setAddress2(ev) {
    let _temp = ev.target.value.length ? ev.target.value : null;
    setLocation({ ...location, address2: _temp });
    if (ev.target.value) {
      setErrors((prev) => delete prev.address2);
    }
  }

  function setCity(ev) {
    let isValid = /^[a-zA-Z \-']+$/.test(ev.target.value);
    let value = ev.target.value;
    let empty = "";

    if (isValid || value === empty) {
      setLocation({ ...location, city: ev.target.value });
    }
    if (ev.target.value) {
      setErrors((prev) => delete prev.city);
    }
  }

  function setTheState(value) {
    setLocation({ ...location, state: value });
    if (value) {
      setErrors((prev) => delete prev.state);
    }
  }

  function checkForNumbers(e) {
    let isValid = /^[0-9 ]+$/.test(e.target.value);
    let value = e.target.value;
    let empty = "";
    if (isValid || value === empty) {
      return true;
    }
    return false;
  }

  function setZip5(ev) {
    if (checkForNumbers(ev) && ev.target.value.length < 6) {
      setLocation({ ...location, zip5: ev.target.value });
    }
    if (ev.target.value) {
      setErrors((prev) => delete prev.zip5);
    }
  }

  function setZip4(ev) {
    if (checkForNumbers(ev) && ev.target.value.length < 5) {
      setLocation({ ...location, zip4: ev.target.value });
    }
    if (ev.target.value) {
      setErrors((prev) => delete prev.zip4);
    }
  }

  function onChangeLocationType(ev) {
    setLoadedLocationTypeId(ev.target.value);
    setLocation({ ...location, location_type_id: ev.target.value });
    if (ev.target.value) {
      setErrors((prev) => delete prev.locationType);
    }
  }

  function getSvgImgSrc(svg) {
    const buff = Buffer.from(svg);
    const base64data = buff.toString("base64");
    return `data:image/svg+xml;base64,${base64data}`;
  }

  const goBack = () => {
    if(locationDataChanged) {
      setShowUnsavedModal(true);
    } else {
      history.push("/locations");
    }
  };

  const confirmedGoBack = () => {
    setLocationDataChanged(false);
    setShowUnsavedModal(false);
    setTimeout(() => {
      history.push(nextUrl.current);
    }, 200);
  }

  let nextUrl = useRef("/locations");

  useEffect(() => {
    const checkBeforeLeave = () => {
      if(locationDataChanged) {
        setShowUnsavedModal(true);
      } else {
        history.push(nextUrl.current);
      }
    };
    const unblock = history.block((location) => {
      nextUrl.current = location.pathname;
      if (locationDataChanged) {
        checkBeforeLeave();
        return false;
      } else {
        return true;
      }
    });

    return () => {
      unblock();
    };
}, [history, locationDataChanged]);

  useEffect(() => {
    // keep global sync with local
    setIsGlobalStateSaved(!locationDataChanged);
    return () => {
      // when leaving component reset global
      setIsGlobalStateSaved(true);
    };
    // eslint-disable-next-line
  }, [locationDataChanged])

  useEffect(() => {
    setLocationDataChanged(false);
  }, [session.appState.activeCompany.id])

  const onLocationHover = () => {
    setShowLocationPopover(true);
  };

  const onLocationLeave = () => {
    setShowLocationPopover(false);
  };

  return (
    <form className="page location-form-page" onSubmit={e => e.preventDefault()}>
      <div className="list-header sticky-page-header flex-header">
        <button type="button" className="new-button" onClick={goBack}>
          <i className="bi bi-chevron-left"></i>{" "} Locations
        </button>
        <h1>Location Details</h1>
        <i className="spacer" />
        {access('location_delete') && 
          <button
            type="button"
            className="new-location-button"
            onClick={askDeleteConfirmation}
            disabled={location.id < 1}
          >
            Delete Location
          </button>
        }
        
        <button
          type="button"
          className="new-location-button"
          disabled={!locationDataChanged}
          onClick={onClickSave}
        >
          Save Changes
        </button>
      </div>

      <section className="location-form-details">
        <div className="flex-inline">
          {systemLocationTypeList &&
            systemLocationTypeListLoaded &&
            companyLocationTypeList &&
            companyLocationTypeListLoaded && (
              <>
                <Field
                  label="* Location Type"
                  className="location-form-details-heading"
                  error={errors.locationType}
                >
                  <select
                    onChange={onChangeLocationType}
                    className="location-select"
                    value={location.location_type_id}
                  >
                    <option />
                    {systemLocationTypeList.map((c) => (
                      <option value={c.id} key={c.id}>
                        {c.name}
                      </option>
                    ))}
                    {companyLocationTypeList.map((c) => (
                      <option value={c.id} key={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <i
                  className="bi bi-info-circle location-type-info"
                  onMouseEnter={onLocationHover}
                  onMouseLeave={onLocationLeave}
                ></i>
                <Popover
                  message="Contact Customer Success if you need a different Location Type."
                  show={showLocationPopover}
                  left={-150}
                  top={34}
                />
              </>
            )}
          {locationType !== undefined && (
            <span>
              ICON:
              <img
                className="location-type-icon"
                alt="icon"
                src={getSvgImgSrc(
                  recolor_svg(
                    locationType.svg,
                    locationType.color,
                    "currentColor"
                  )
                )}
              />
              &nbsp;
            </span>
          )}
        </div>

        <Field
          label="* Name"
          className="location-form-details-heading"
          error={errors.name}
        >
          <input
            type="text"
            value={location.name}
            className="location-form-details-info"
            onChange={setName}
          />
        </Field>
        <Field
          label="* Address 1"
          className="location-form-details-heading"
          error={errors.address1}
        >
          <input
            type="text"
            value={location.address1}
            className="location-form-details-info"
            onChange={setAddress1}
          />
        </Field>
        <Field
          label=" Address 2"
          className="location-form-details-heading"
          error={errors.address2}
        >
          <input
            type="text"
            value={location.address2 || ""}
            className="location-form-details-info"
            onChange={setAddress2}
          />
        </Field>
        <Field
          label="* City"
          className="location-form-details-heading"
          error={errors.city}
        >
          <input
            type="text"
            value={location.city}
            className="location-form-details-info"
            onChange={setCity}
          />
        </Field>
        <Field
          label="* State"
          className="location-form-details-heading"
          error={errors.state}
        >
          <select
            onChange={(e) => setTheState(e.target.value)}
            value={location.state}
            className="location-select"
          >
            <option />
            {states.map((st) => (
              <option value={st.code} key={st.code}>
                {st.name}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="* Zip 5"
          className="location-form-details-heading"
          error={errors.zip5}
        >
          <input
            type="text"
            value={location.zip5}
            className="location-form-details-info"
            onChange={setZip5}
          />
        </Field>
        <Field
          label=" Zip 4"
          className="location-form-details-heading"
          error={errors.zip4}
        >
          <input
            type="text"
            value={location.zip4}
            className="location-form-details-info"
            onChange={setZip4}
          />
        </Field>

      </section>
      <Modal centered backdrop isOpen={showUnsavedModal}>
        <ModalHeader>
            Unsaved Location
        </ModalHeader>
        <ModalBody>
            <h6>Your location changes will be lost, continue?</h6>
        </ModalBody>
        <ModalFooter>
          <Button onClick={confirmedGoBack}>Continue</Button>
          <Button className="promote" onClick={() => setShowUnsavedModal(false)}>Return to Editing</Button>
        </ModalFooter>
      </Modal>
      <ConfirmAction 
        show={confirmDeleteModal}
        title="Confirm Delete Location"
        bodyText={
          <div className="text-center">
            <h6>Click <b>Continue</b> to delete this location</h6> 
            <div>or</div>
            <h6>Click <b>Cancel</b> to go back to editing.</h6>
          </div>}
        onConfirm={onClickDelete}
      />
      <LocationRebuildNotice show={showRebuildNotice} label={succesLabel} onClose={onCloseRebuildNotice} />
    </form>
  );
}
